import React, { useEffect, useState } from 'react';

interface ReactCardFlipProps {
    isFlipped?: boolean;
    flipDirection?: 'horizontal' | 'vertical';
    containerStyle?: React.CSSProperties;
    cardStyles?: {
        front?: React.CSSProperties;
        back?: React.CSSProperties;
    };
    cardZIndex?: string | number;
    flipSpeedFrontToBack?: number;
    flipSpeedBackToFront?: number;
    infinite?: boolean;
    children: [React.ReactNode, React.ReactNode];
}

const ReactCardFlip: React.FC<ReactCardFlipProps> = ({
    isFlipped = false,
    flipDirection = 'horizontal',
    containerStyle = {},
    cardStyles = { front: {}, back: {} },
    cardZIndex = 'auto',
    flipSpeedFrontToBack = 0.6,
    flipSpeedBackToFront = 0.6,
    infinite = false,
    children,
}) => {
    const [flipped, setFlipped] = useState(isFlipped);
    const [rotation, setRotation] = useState(0);

    useEffect(() => {
        if (isFlipped !== flipped) {
            setFlipped(isFlipped);
            setRotation(c => c + 180);
        }
    }, [isFlipped]);

    const frontRotateY = `rotateY(${infinite ? rotation : flipped ? 180 : 0}deg)`;
    const backRotateY = `rotateY(${infinite ? rotation + 180 : flipped ? 0 : -180}deg)`;
    const frontRotateX = `rotateX(${infinite ? rotation : flipped ? 180 : 0}deg)`;
    const backRotateX = `rotateX(${infinite ? rotation + 180 : flipped ? 0 : -180}deg)`;

    const { front: frontStyle = {}, back: backStyle = {} } = cardStyles;

    const styles: { [key: string]: React.CSSProperties } = {
        container: {
            perspective: '1000px',
            zIndex: `${cardZIndex}`,
        },
        flipper: {
            position: 'relative',
            width: '100%',
            height: '100%',
        },
        front: {
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
            left: '0',
            position: flipped ? 'absolute' : 'relative',
            top: '0',
            transform: flipDirection === 'horizontal' ? frontRotateY : frontRotateX,
            transformStyle: 'preserve-3d',
            width: '100%',
            height: '100%',
            zIndex: '2',
            transition: `${flipSpeedBackToFront}s`,
            ...frontStyle,
        },
        back: {
            WebkitBackfaceVisibility: 'hidden',
            backfaceVisibility: 'hidden',
            left: '0',
            position: flipped ? 'relative' : 'absolute',
            transform: flipDirection === 'horizontal' ? backRotateY : backRotateX,
            transformStyle: 'preserve-3d',
            top: '0',
            width: '100%',
            height: '100%',
            transition: `${flipSpeedFrontToBack}s`,
            ...backStyle,
        },
    };

    return (
        <div className="react-card-flip" style={{ ...styles.container, ...containerStyle }}>
            <div className="react-card-flipper" style={styles.flipper}>
                <div className="react-card-front" style={styles.front}>{children[0]}</div>
                <div className="react-card-back" style={styles.back}>{children[1]}</div>
            </div>
        </div>
    );
};

export default ReactCardFlip;
